import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { usePlaidLink } from "react-plaid-link";
import { getAccountLinks, unlinkAccount, getReconnectToken, markReconnectSuccess, getAccounts, getHiddenAccounts, hideAccount, unhideAccount, checkConnectionHealth } from "../../api/plaidClient";
import LoadingSpinner from "../ui/LoadingSpinner";
import ConnectionStatusBadge, { ConnectionHealthAlert } from "../ui/ConnectionStatusBadge";
import { formatAccountSubtype } from "../../utils/formatters";
import { useSubscription } from "../../contexts/SubscriptionContext";

function SubscribeRequiredCard() {
    return (
        <div className="card p-8 text-center">
            <div className="w-12 h-12 bg-accent-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-accent-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                    <rect x="3" y="11" width="18" height="11" rx="2" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Subscribe to manage accounts
            </h3>
            <p className="text-gray-600 max-w-md mx-auto mb-6">
                Linking banks, brokerages, and syncing balances requires an active subscription.
                Start your 14-day free trial to continue.
            </p>
            <Link to="/subscribe" className="btn-primary px-6 py-2.5 text-sm inline-flex">
                Go to subscription
            </Link>
        </div>
    );
}

export default function AccountManagement({ onAccountsChange }) {
    const { hasActiveSubscription, loading: subLoading } = useSubscription();
    const [links, setLinks] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [hiddenIds, setHiddenIds] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [unlinkingId, setUnlinkingId] = useState(null);
    const [reconnectToken, setReconnectToken] = useState(null);
    const [reconnectingId, setReconnectingId] = useState(null);
    const [togglingId, setTogglingId] = useState(null);
    const [showHidden, setShowHidden] = useState(false);
    const [checkingHealthId, setCheckingHealthId] = useState(null);
    const abortControllerRef = useRef(null);
    const fetchedInfoRef = useRef(new Set());

    const fetchData = useCallback(async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        try {
            const [linksData, accountsData, hiddenData] = await Promise.all([
                getAccountLinks(signal),
                getAccounts(false, signal),
                getHiddenAccounts(signal)
            ]);
            setLinks(linksData);
            setAccounts(accountsData || []);
            setHiddenIds(new Set(hiddenData || []));
        } catch (err) {
            if (err.name === 'AbortError' || err.name === 'CanceledError') {
                return;
            }
            setError("Failed to load account links");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Don't attempt to fetch while subscription state is still resolving,
        // and skip entirely when the user has no active subscription (the
        // backend rejects these endpoints for unsubscribed users).
        if (subLoading) return;
        if (!hasActiveSubscription) {
            setLoading(false);
            return;
        }
        fetchData();
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [fetchData, subLoading, hasActiveSubscription]);

    // Auto-fetch institution info for links that are missing it
    useEffect(() => {
        if (loading) return;

        const linksNeedingInfo = links.filter(
            link => !link.institutionDisplayName && !fetchedInfoRef.current.has(link.id)
        );

        if (linksNeedingInfo.length === 0) return;

        const fetchMissingInfo = async () => {
            for (const link of linksNeedingInfo) {
                fetchedInfoRef.current.add(link.id);
                try {
                    const updatedLink = await checkConnectionHealth(link.id);
                    setLinks(prev => prev.map(l => l.id === link.id ? updatedLink : l));
                } catch (err) {
                    // Silently fail - user can manually refresh if needed
                }
            }
        };
        fetchMissingInfo();
    }, [links, loading]);

    const handleUnlink = async (linkId) => {
        if (!window.confirm("Are you sure you want to unlink this account? This will remove all associated data.")) {
            return;
        }

        setUnlinkingId(linkId);
        setError("");
        try {
            await unlinkAccount(linkId);
            await fetchData();
            if (onAccountsChange) onAccountsChange();
        } catch (err) {
            setError("Failed to unlink account");
        } finally {
            setUnlinkingId(null);
        }
    };

    const handleToggleHidden = async (accountId) => {
        setTogglingId(accountId);
        setError("");
        try {
            if (hiddenIds.has(accountId)) {
                await unhideAccount(accountId);
                setHiddenIds(prev => {
                    const next = new Set(prev);
                    next.delete(accountId);
                    return next;
                });
            } else {
                await hideAccount(accountId);
                setHiddenIds(prev => new Set([...prev, accountId]));
            }
            if (onAccountsChange) onAccountsChange();
        } catch (err) {
            setError("Failed to update account visibility");
        } finally {
            setTogglingId(null);
        }
    };

    const handleReconnect = async (linkId) => {
        setReconnectingId(linkId);
        setError("");
        try {
            const token = await getReconnectToken(linkId);
            setReconnectToken(token);
        } catch (err) {
            setError("Failed to get reconnect token. The account may need to be unlinked and re-added.");
            setReconnectingId(null);
        }
    };

    const handleCheckHealth = async (linkId) => {
        setCheckingHealthId(linkId);
        setError("");
        try {
            const updatedLink = await checkConnectionHealth(linkId);
            setLinks(prev => prev.map(l => l.id === linkId ? updatedLink : l));
        } catch (err) {
            setError("Failed to check connection health");
        } finally {
            setCheckingHealthId(null);
        }
    };

    const onReconnectSuccess = useCallback(async () => {
        const linkId = reconnectingId;
        setReconnectToken(null);
        setReconnectingId(null);
        if (linkId) {
            try {
                await markReconnectSuccess(linkId);
            } catch (err) {
                console.error("Failed to mark reconnect success:", err);
            }
        }
        await fetchData();
        if (onAccountsChange) onAccountsChange();
    }, [fetchData, onAccountsChange, reconnectingId]);

    const onReconnectExit = useCallback(() => {
        setReconnectToken(null);
        setReconnectingId(null);
    }, []);

    // Plaid Link for reconnection
    const { open: openReconnect, ready: reconnectReady } = usePlaidLink({
        token: reconnectToken,
        onSuccess: onReconnectSuccess,
        onExit: onReconnectExit,
    });

    useEffect(() => {
        if (reconnectToken && reconnectReady) {
            openReconnect();
        }
    }, [reconnectToken, reconnectReady, openReconnect]);

    if (loading || subLoading) {
        return (
            <div className="flex justify-center py-8">
                <LoadingSpinner />
            </div>
        );
    }

    if (!hasActiveSubscription) {
        return <SubscribeRequiredCard />;
    }

    const visibleAccounts = showHidden ? accounts : accounts.filter(a => !hiddenIds.has(a.accountId));
    const hiddenCount = accounts.filter(a => hiddenIds.has(a.accountId)).length;

    return (
        <div className="space-y-6">
            {/* Institution Links Section */}
            <div>
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Linked Institutions</h2>
                        <p className="text-sm text-gray-500">Manage your bank connections</p>
                    </div>
                </div>

                {error && (
                    <div className="p-3 mb-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-danger-500">{error}</p>
                    </div>
                )}

                <ConnectionHealthAlert links={links} />

                {links.length === 0 ? (
                    <div className="card p-6 text-center">
                        <p className="text-gray-500">No accounts linked yet.</p>
                        <p className="text-sm text-gray-500 mt-1">Use the "Link Account" button to connect your financial accounts.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {links.map((link) => {
                            const isErrored = link.connectionStatus === "ERROR" || link.connectionStatus === "NEEDS_REAUTH";
                            const isInactive = !link.active;
                            const needsAction = isErrored || isInactive;

                            return (
                                <div
                                    key={link.id}
                                    className={`card p-4 ${needsAction ? "border-red-200 bg-red-50" : ""}`}
                                >
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {link.institutionLogoUrl && (
                                                    <img
                                                        src={link.institutionLogoUrl}
                                                        alt=""
                                                        className="w-6 h-6 rounded"
                                                        onError={(e) => e.target.style.display = 'none'}
                                                    />
                                                )}
                                                <h3 className={`font-medium ${needsAction ? "text-gray-700" : "text-gray-900"}`}>
                                                    {link.institutionDisplayName || link.institutionId || `Account Link #${link.id}`}
                                                </h3>
                                                <ConnectionStatusBadge status={link.connectionStatus} />
                                                {isInactive && (
                                                    <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-500 rounded">
                                                        Inactive
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                                                <span>Connected {new Date(link.createdAt).toLocaleDateString()}</span>
                                                {link.lastSuccessfulSyncAt && (
                                                    <span>Last synced {new Date(link.lastSuccessfulSyncAt).toLocaleString()}</span>
                                                )}
                                            </div>
                                            {link.needsAttention && link.lastErrorMessage && (
                                                <p className="text-sm text-amber-700 mt-2 bg-amber-50 px-3 py-2 rounded">
                                                    {link.lastErrorMessage}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex gap-2 ml-4">
                                            <button
                                                onClick={() => handleCheckHealth(link.id)}
                                                disabled={checkingHealthId === link.id}
                                                className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                                                title="Check connection health"
                                            >
                                                {checkingHealthId === link.id ? (
                                                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                                    </svg>
                                                ) : (
                                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                    </svg>
                                                )}
                                            </button>
                                            {needsAction ? (
                                                <button
                                                    onClick={() => handleReconnect(link.id)}
                                                    disabled={reconnectingId === link.id}
                                                    className="px-3 py-1.5 text-sm bg-amber-100 text-amber-600 rounded-lg hover:bg-amber-200 font-medium transition-colors disabled:opacity-50"
                                                >
                                                    {reconnectingId === link.id ? "..." : "Reconnect"}
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => handleReconnect(link.id)}
                                                    disabled={reconnectingId === link.id}
                                                    className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                                                >
                                                    {reconnectingId === link.id ? "..." : "Reconnect"}
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleUnlink(link.id)}
                                                disabled={unlinkingId === link.id}
                                                className="px-3 py-1.5 text-sm bg-red-50 text-danger-500 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                                            >
                                                {unlinkingId === link.id ? "Unlinking..." : "Unlink"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Individual Accounts Section */}
            {accounts.length > 0 && (
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Individual Accounts</h2>
                            <p className="text-sm text-gray-500">Hide accounts you don't want to track</p>
                        </div>
                        {hiddenCount > 0 && (
                            <button
                                onClick={() => setShowHidden(!showHidden)}
                                className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
                            >
                                {showHidden ? "Hide" : "Show"} {hiddenCount} hidden
                            </button>
                        )}
                    </div>

                    <div className="space-y-2">
                        {visibleAccounts.map((account) => {
                            const isHidden = hiddenIds.has(account.accountId);
                            return (
                                <div
                                    key={account.accountId}
                                    className={`card p-3 flex justify-between items-center ${isHidden ? "opacity-50" : ""}`}
                                >
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-gray-900">{account.name}</span>
                                            <span className="text-xs text-gray-500">
                                                {formatAccountSubtype(account.subtype)}
                                            </span>
                                            {isHidden && (
                                                <span className="px-1.5 py-0.5 text-xs bg-gray-200 text-gray-500 rounded">
                                                    Hidden
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-500">
                                            ****{account.mask || "----"}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => handleToggleHidden(account.accountId)}
                                        disabled={togglingId === account.accountId}
                                        className={`px-3 py-1.5 text-sm rounded-lg transition-colors disabled:opacity-50 ${
                                            isHidden
                                                ? "bg-accent-50 text-accent-600 hover:bg-accent-100"
                                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                        }`}
                                    >
                                        {togglingId === account.accountId ? "..." : isHidden ? "Show" : "Hide"}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="text-sm text-gray-500 space-y-1">
                <p><strong>Reconnect:</strong> Re-authenticate if your bank requires it.</p>
                <p><strong>Unlink:</strong> Remove institution and all its accounts.</p>
                <p><strong>Hide:</strong> Exclude specific accounts from your dashboard and calculations.</p>
            </div>
        </div>
    );
}
